import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { GRANT_GUEST_PASS_TOKENS } from "../gql/mutations/user";
import { ADMIN_GUEST_PASS_SUMMARY } from "../gql/queries/user";

interface GuestPassSummary {
  id: string;
  endsAt: string;
  daysRemaining: number;
  tokenAllowance: number;
  tokensUsed: number;
  tokensRemaining: number;
  isActive: boolean;
  canActivateMembership: boolean;
}

interface GuestPassTokenAdjustment {
  id: string;
  amount: number;
  reason: string;
  grantedById: string;
  grantedByName?: string | null;
  grantedByEmail?: string | null;
  createdAt: string;
}

export default function GuestPassAdminCard({ userId }: { userId: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [amount, setAmount] = useState(1);
  const [reason, setReason] = useState("");
  const { data, loading, refetch } = useQuery<{
    adminGuestPassSummary?: GuestPassSummary | null;
    adminGuestPassTokenAdjustments: GuestPassTokenAdjustment[];
  }>(ADMIN_GUEST_PASS_SUMMARY, {
    variables: { userId },
    fetchPolicy: "network-only",
  });
  const [grantTokens, { loading: granting }] = useMutation(GRANT_GUEST_PASS_TOKENS);

  if (loading) {
    return <CircularProgress size={24} />;
  }
  const guestPass = data?.adminGuestPassSummary;
  const tokenAdjustments = data?.adminGuestPassTokenAdjustments ?? [];
  if (!guestPass) {
    return null;
  }

  const submit = async () => {
    try {
      await grantTokens({ variables: { input: { userId, amount, reason } } });
      await refetch();
      setReason("");
      enqueueSnackbar(`${amount} Guest Pass token${amount === 1 ? "" : "s"} added`, {
        variant: "success",
      });
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : "Unable to add Guest Pass tokens", {
        variant: "error",
      });
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3 }} elevation={1}>
      <Typography variant="h5" mb={2}>
        Guest Pass
      </Typography>
      <Box display="flex" gap={3} flexWrap="wrap" mb={2}>
        <Typography>Days remaining: {guestPass.daysRemaining}</Typography>
        <Typography>Allowance: {guestPass.tokenAllowance}</Typography>
        <Typography>Used: {guestPass.tokensUsed}</Typography>
        <Typography>Remaining: {guestPass.tokensRemaining}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Ends {new Date(guestPass.endsAt).toLocaleString()}
      </Typography>
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "140px 1fr auto" }} gap={2}>
        <TextField
          label="Extra tokens"
          type="number"
          inputProps={{ min: 1, step: 1 }}
          value={amount}
          onChange={(event) => setAmount(Number(event.target.value))}
          disabled={!guestPass.isActive || granting}
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Technical issue, host no-show…"
          disabled={!guestPass.isActive || granting}
        />
        <Button
          variant="contained"
          disabled={!guestPass.isActive || granting || amount < 1 || !reason.trim()}
          onClick={() => void submit()}
        >
          Add tokens
        </Button>
      </Box>
      {!guestPass.isActive ? (
        <Typography color="error" mt={2}>
          Tokens can only be added while the member is in an active trial.
        </Typography>
      ) : null}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" mb={1}>
        Token adjustment history
      </Typography>
      {tokenAdjustments.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No tokens have been added by an administrator.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Guest Pass token adjustment history">
            <TableHead>
              <TableRow>
                <TableCell>Tokens added</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Added by</TableCell>
                <TableCell>Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tokenAdjustments.map((adjustment) => (
                <TableRow key={adjustment.id}>
                  <TableCell>+{adjustment.amount}</TableCell>
                  <TableCell>{adjustment.reason}</TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {adjustment.grantedByName || adjustment.grantedByEmail || "Unknown admin"}
                    </Typography>
                    {adjustment.grantedByName && adjustment.grantedByEmail ? (
                      <Typography variant="caption" color="text.secondary">
                        {adjustment.grantedByEmail}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>{new Date(adjustment.createdAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
